// Satır doğrulama (spec §1, §3): kaynak tipi alanları, normalizasyon, hata listesi. Saf fonksiyon, G/Ç yok.
// Alan adları: docs/design/glossary.md §5. Hata adları: ADR-0005.
import {
  type DateFormat, type EnumField, type NumberFormat, ENUMS, cellValue, normalizeDocNo, normalizeEnum, normalizeUnit,
  parseDate, parseNumber, parseTime, suggestDateFormat, suggestNumberFormat, NUMBER_FORMATS,
} from "./normalize";

import { recordKeyOf } from "./versions";
import type { FormatQuestion, RowError, UnknownValues } from "./api";
export type { FormatQuestion, RowError, UnknownValues }; // contract types live in shared/api.ts

export const MAX_ROW_ERRORS = 20;

type Kind = "text" | "docNo" | "number" | "date" | "time" | "unit" | EnumField;
interface Spec {
  /** zorunlu alanlar */ required: string[];
  /** isteğe bağlı alanlar */ optional: string[];
  kinds: Record<string, Kind>;
  /** kayıt kimliği parçaları (spec §1, ADR-0003) */ key: string[];
  /** koşullu zorunlu: koşulu sağlayan satırda boşsa hata */ conditional?: { field: string; when: (f: Fields) => boolean }[];
}
export type Fields = Record<string, string | number | null>;

const customs = { quantity: "number", unit: "unit", declarationNo: "docNo", registrationDate: "date", itemNo: "text", hsCode: "text", ipaNo: "docNo", statisticalValue: "number", currency: "text", procedureCode: "text" } as const;
const D1_D2_REQ = ["declarationNo", "registrationDate", "itemNo", "hsCode", "quantity", "unit"];
const D1_D2_OPT = ["ipaNo", "statisticalValue", "currency", "procedureCode"];

export const SOURCE_TYPES = {
  GUMRUK_ITH: { required: D1_D2_REQ, optional: [...D1_D2_OPT, "originCountry"], kinds: { ...customs, originCountry: "text" }, key: ["declarationNo", "itemNo"] },
  // Kalem No yoksa ya da boşsa 1 kabul edilir (spec §1).
  GUMRUK_IHR: { required: D1_D2_REQ.filter((f) => f !== "itemNo"), optional: ["itemNo", ...D1_D2_OPT, "destinationCountry"], kinds: { ...customs, destinationCountry: "text" }, key: ["declarationNo", "itemNo"] },
  ERP_STOK: {
    required: ["movementNo", "date", "movementType", "materialCode", "quantity", "unit"], optional: ["referenceDoc", "workOrderNo", "warehouse"],
    kinds: { movementNo: "text", date: "date", movementType: "movementType", materialCode: "text", quantity: "number", unit: "unit", referenceDoc: "docNo", workOrderNo: "text", warehouse: "text" },
    key: ["movementNo"],
    conditional: [
      { field: "referenceDoc", when: (f) => f.movementType === "goods_receipt" || f.movementType === "shipment" },
      { field: "workOrderNo", when: (f) => f.movementType === "consumption" || f.movementType === "production" },
    ],
  },
  RECETE: { required: ["productCode", "componentCode", "quantity", "unit", "wastePct"], optional: [], kinds: { productCode: "text", componentCode: "text", quantity: "number", unit: "unit", wastePct: "number" }, key: ["productCode", "componentCode"] },
  DIIB: {
    required: ["ipaNo", "startDate", "endDate", "commitmentType", "hsCode", "quantity", "unit"], optional: ["valueUsd"],
    kinds: { ipaNo: "docNo", startDate: "date", endDate: "date", commitmentType: "commitmentType", hsCode: "text", quantity: "number", unit: "unit", valueUsd: "number" },
    key: ["ipaNo", "commitmentType", "hsCode"],
  },
  // Kimlik: Fiş No + satır sırası. lineSeq sistemce üretilir (aynı fişin dosyadaki kaçıncı satırı).
  MUHASEBE: { required: ["voucherNo", "date", "debit", "credit", "documentNo"], optional: ["accountCode"], kinds: { voucherNo: "docNo", date: "date", debit: "number", credit: "number", documentNo: "docNo", accountCode: "text" }, key: ["voucherNo", "lineSeq"] },
  GUVENLIK: {
    required: ["entryNo", "date", "time", "entryType", "gate", "direction"], optional: ["plate"],
    kinds: { entryNo: "text", date: "date", time: "time", entryType: "entryType", gate: "text", direction: "direction", plate: "text" },
    key: ["entryNo"], conditional: [{ field: "plate", when: (f) => f.entryType === "vehicle" }],
  },
  KAMERA: { required: ["date", "cameraCode", "cameraLocation", "outageMin"], optional: ["totalMin", "cameraStatus"], kinds: { date: "date", cameraCode: "text", cameraLocation: "text", outageMin: "number", totalMin: "number", cameraStatus: "text" }, key: ["date", "cameraCode"] },
  GTIP_MALZEME: { required: ["hsCode", "materialCode", "conversionFactor", "unit"], optional: [], kinds: { hsCode: "text", materialCode: "text", conversionFactor: "number", unit: "unit" }, key: ["hsCode", "materialCode"] },
} as const satisfies Record<string, Spec>;
export type SourceType = keyof typeof SOURCE_TYPES;

export interface Profile {
  numberFormat?: NumberFormat;
  dateFormat?: DateFormat;
  /** { movementType: { "Mal Kabul": "goods_receipt" } } */
  valueMap?: Partial<Record<EnumField, Record<string, string>>>;
}
/** Fixed format of manual entry and browser validation (ADR-0016): JSON numbers, YYYY-MM-DD dates, categorical values are glossary codes. */
export const PLAIN: Profile = {
  numberFormat: "plain", dateFormat: "yyyy-mm-dd",
  valueMap: Object.fromEntries((Object.keys(ENUMS) as EnumField[]).map((f) => [f, Object.fromEntries(Object.values(ENUMS[f]).map((c) => [c, c]))])),
};
export interface RawRow { rowNo: number; cells: Record<string, unknown> }
export interface NormalizedRecord { rowNo: number; key: string[]; fields: Fields }
export type ValidationResult = {
  ok: boolean;
  records: NormalizedRecord[];
  rowErrors: RowError[]; // en çok 20 öğe
  totalErrors: number;
  missingFields: string[]; // eşlenmemiş zorunlu alan
  questions: FormatQuestion[]; // AMBIGUOUS_FORMAT
  unknownValues: UnknownValues[]; // VALUE_MAP_MISSING
  /** Kullanılan ya da önerilen biçim, alan başına (profile kaydedilir). */
  numberFormats: Record<string, NumberFormat>;
};

const show = (v: unknown) => (v instanceof Date ? v.toISOString() : v === null || v === undefined ? "" : String(v));
const round = (n: number) => Number(n.toFixed(9));

/** `window`: dönem yüklemesi (ADR-0018); dönem dışı satır `OUTSIDE_PERIOD` satır hatasıdır.
 * `mapping`: sistem alanı → dosya kolon adı. Satır hatası varsa `ok` false: dosya yüklenmez, kısmi sonuç yok. */
export function validateRows(sourceType: SourceType, rows: RawRow[], mapping: Record<string, string>, profile: Profile = {}, window?: { dateField: string; start: string; end: string }): ValidationResult {
  const spec: Spec = SOURCE_TYPES[sourceType];
  const mapped = (f: string) => mapping[f] !== undefined;
  const missingFields = spec.required.filter((f) => !mapped(f));
  const questions: FormatQuestion[] = [];
  const numberFormats: Record<string, NumberFormat> = {};
  const dateFormats: Record<string, DateFormat | null> = {};
  const fieldsOf = [...spec.required, ...spec.optional].filter(mapped);
  const col = (r: RawRow, f: string) => cellValue(r.cells[mapping[f]!]);

  // Biçimler: profilden, yoksa kolon örneklerinden. Belirsizse soru, o kolonun metin değerleri çözülmez.
  for (const f of fieldsOf) {
    const kind = spec.kinds[f]!;
    const samples = () => rows.map((r) => col(r, f));
    if (kind === "number") {
      const fmt = profile.numberFormat ?? (() => { const s = suggestNumberFormat(samples()); if (s.status === "ambiguous") questions.push({ field: f, column: mapping[f]!, sample: s.sample, options: [...NUMBER_FORMATS] }); return s.status === "ok" ? s.format : undefined; })();
      if (fmt) numberFormats[f] = fmt;
    } else if (kind === "date") {
      const s = suggestDateFormat(samples());
      dateFormats[f] = profile.dateFormat ?? (s.status === "ok" ? s.format : null);
    }
  }

  const errors: RowError[] = [];
  const unknown: Record<string, Set<string>> = {};
  const records: NormalizedRecord[] = [];
  const lineSeq = new Map<string, number>();
  const err = (r: RawRow, f: string | undefined, reason: string, v: unknown) => errors.push({ rowNo: r.rowNo, column: f ? mapping[f] ?? f : "", value: show(v), reason });

  for (const r of rows) {
    if (fieldsOf.every((f) => col(r, f) === null)) continue; // boş satır
    const out: Fields = {};
    const before = errors.length;
    const factorOf = { factor: 1 };
    for (const f of fieldsOf) {
      const kind = spec.kinds[f]!;
      const v = col(r, f);
      if (v === null) {
        out[f] = f === "itemNo" && sourceType === "GUMRUK_IHR" ? "1" : null;
        if (spec.required.includes(f)) err(r, f, "REQUIRED_EMPTY", "");
        continue;
      }
      if (kind === "text") { typeof v === "string" || typeof v === "number" ? (out[f] = String(v).trim()) : err(r, f, "INVALID_TEXT", v); continue; }
      const res =
        kind === "docNo" ? normalizeDocNo(v)
        : kind === "number" ? (numberFormats[f] ? parseNumber(v, numberFormats[f]!) : typeof v === "number" ? parseNumber(v, "plain") : null)
        : kind === "date" ? parseDate(v, dateFormats[f] ?? null)
        : kind === "time" ? parseTime(v)
        : kind === "unit" ? normalizeUnit(v)
        : normalizeEnum(kind, v, profile.valueMap?.[kind]);
      if (res === null) continue; // belirsiz biçim: soru döner, değer çözülmez
      if (!res.ok) {
        if (res.reason === "UNKNOWN_VALUE") (unknown[f] ??= new Set()).add(show(v).trim());
        else err(r, f, res.reason, v);
        continue;
      }
      if (kind === "unit") {
        const u = res.value as { unit: string; factor: number };
        // D9: TON katsayı yönü netleşene kadar satır hatası (kullanıcı kararı).
        if (sourceType === "GTIP_MALZEME" && u.factor !== 1) { err(r, f, "UNKNOWN_UNIT", v); continue; }
        out[f] = u.unit; factorOf.factor = u.factor;
      }
      else out[f] = res.value as string | number;
    }
    // TON → KG: miktar ×1000. Yalnız `quantity`.
    if (factorOf.factor !== 1 && typeof out.quantity === "number") out.quantity = round(out.quantity * factorOf.factor);
    if (sourceType === "GUMRUK_IHR") out.itemNo ??= "1"; // kolon hiç eşlenmemişse de
    for (const c of spec.conditional ?? []) if (c.when(out) && (!mapped(c.field) || col(r, c.field) === null)) err(r, c.field, "REQUIRED_EMPTY", "");
    if (sourceType === "MUHASEBE" && out.voucherNo != null) {
      const n = (lineSeq.get(String(out.voucherNo)) ?? 0) + 1;
      lineSeq.set(String(out.voucherNo), n);
      out.lineSeq = n;
    }
    // ADR-0018: a period upload takes only rows whose event date lies in the period; listed with the other row errors.
    if (window && errors.length === before) {
      const d = out[window.dateField];
      if (!(typeof d === "string" && d.slice(0, 10) >= window.start && d.slice(0, 10) <= window.end)) err(r, window.dateField, "OUTSIDE_PERIOD", d ?? "");
    }
    if (errors.length === before) records.push({ rowNo: r.rowNo, key: spec.key.map((k) => String(out[k])), fields: out });
  }

  // Tekrar eden kayıt kimliği: kimlik başına tek öğe, bütün satırlarıyla (spec §3). Hatalı satırlar kimliksizdir, gruba girmez.
  const groups = new Map<string, NormalizedRecord[]>();
  for (const rec of records) { const k = recordKeyOf(rec.key); groups.set(k, [...(groups.get(k) ?? []), rec]); }
  for (const [k, g] of groups) {
    if (g.length > 1) errors.push({ rowNo: g[0]!.rowNo, column: spec.key.map((f) => mapping[f] ?? f).join(", "), value: k, reason: "DUPLICATE_KEY", duplicateRows: g.map((x) => x.rowNo) });
  }
  errors.sort((a, b) => a.rowNo - b.rowNo);

  const unknownValues = Object.entries(unknown).map(([field, v]) => ({ field, column: mapping[field]!, values: [...v] }));
  const ok = !errors.length && !questions.length && !unknownValues.length && !missingFields.length;
  return { ok, records: ok ? records : [], rowErrors: errors.slice(0, MAX_ROW_ERRORS), totalErrors: errors.length, missingFields, questions, unknownValues, numberFormats };
}

